import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MdMenuOpen,
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
} from "react-icons/md";
import "./style.css";
import routes from "../../Utils/routes";
import { filterRoutesByPermissions, getStoredUser } from "../../Utils/permissionAccess";

export default function Sidebar({ isOpen, onToggle, permissionUser }) {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const allowedRoutes = useMemo(
    () => filterRoutesByPermissions(routes, permissionUser || getStoredUser()),
    [permissionUser],
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) setExpandedGroups({});
  }, [isOpen]);

  useEffect(() => {
    const currentPath = location.pathname;
    const newExpandedGroups = {};

    const checkActive = (items, parentKey = "") => {
      let found = false;
      items.forEach((item) => {
        if (item.visible === false) return;
        const itemKey = parentKey ? `${parentKey}/${item.label}` : item.label;
        if (item.isGroup && item.children) {
          const childActive = checkActive(item.children, itemKey);
          if (childActive) {
            newExpandedGroups[itemKey] = true;
            found = true;
          }
        } else if (
          currentPath === item.path ||
          (currentPath.startsWith(item.path + "/") && item.path !== "/")
        ) {
          found = true;
        }
      });
      return found;
    };

    if (checkActive(allowedRoutes)) {
      setExpandedGroups(newExpandedGroups);
    }
  }, [allowedRoutes, location.pathname]);

  const toggleGroup = (itemKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  const handleChildClick = () => {
    if (isMobile) onToggle(false);
  };

  const sidebarWidth = isOpen ? "w-64" : "w-16";

  const renderNavItem = (item, level = 0, parentKey = "") => {
    if (item.visible === false) return null;

    const itemKey = parentKey ? `${parentKey}/${item.label}` : item.label;

    const isActive =
      location.pathname === item.path ||
      (location.pathname.startsWith(item.path + "/") && item.path !== "/");

    if (item.isGroup) {
      const expanded = expandedGroups[itemKey];
      const hasActiveChild = (items) =>
        items.some(
          (child) =>
            location.pathname === child.path ||
            (location.pathname.startsWith(child.path + "/") &&
              child.path !== "/") ||
            (child.isGroup && child.children && hasActiveChild(child.children)),
        );

      const isParentOfActive = item.children && hasActiveChild(item.children);

      return (
        <li key={itemKey} className="w-full">
          <button
            onClick={() => toggleGroup(itemKey)}
            className={`w-full flex items-center rounded-md px-4 py-3 transition-all duration-200 transform
            ${
              isParentOfActive
                ? level > 0
                  ? "bg-white/20 shadow-md scale-[1.02] -translate-y-0.5"
                  : "sidebar-active"
                : "hover:bg-white/20 bg-white/10 hover:shadow-md hover:scale-[1.02] hover:-translate-y-0.5"
            }
            ${level > 0 ? "py-2" : ""}
            `}
          >
            <span className="transition-transform duration-200 hover:scale-110 flex-shrink-0">
              {item.icon}
            </span>
            {(isOpen || isMobile) && (
              <span
                className="ml-3 transition-all duration-200 flex-1 min-w-0 text-left text-sm leading-tight"
                style={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  hyphens: "auto",
                }}
              >
                {item.label}
              </span>
            )}
            {(isOpen || isMobile) && (
              <span className="ml-2 transition-transform duration-200 flex-shrink-0">
                {expanded ? (
                  <MdKeyboardArrowDown className="transition-transform duration-200 hover:scale-110" />
                ) : (
                  <MdKeyboardArrowRight className="transition-transform duration-200 hover:scale-110" />
                )}
              </span>
            )}
          </button>
          {expanded && item.children && (
            <ul className="mt-1 space-y-1 ml-4">
              {item.children.map((child) => renderNavItem(child, level + 1, itemKey))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.path || itemKey} className="w-full">
        <Link
          to={item.path}
          onClick={handleChildClick}
          className={`flex items-center rounded-md px-4 py-3 transition-all duration-200 transform relative overflow-hidden
          ${
            isActive
              ? level > 0
                ? "bg-white/20 shadow-md scale-[1.02] -translate-y-0.5"
                : "sidebar-active"
              : "hover:bg-white/20 bg-white/10 hover:shadow-md hover:scale-[1.02] hover:-translate-y-0.5"
          }
          ${level > 0 ? "py-2" : ""}
          before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-brand-secondary before:transform before:scale-y-0 before:transition-transform before:duration-200 hover:before:scale-y-100
        `}
        >
          <span className="transition-transform duration-200 hover:scale-110 relative z-10 flex-shrink-0">
            {item.icon}
          </span>
          {(isOpen || isMobile) && (
            <span
              className={`ml-3 transition-all duration-200 relative z-10 flex-1 min-w-0 text-left leading-tight ${
                level > 0 ? "text-xs" : "text-sm"
              }`}
              style={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                hyphens: "auto",
              }}
            >
              {item.label}
            </span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => onToggle(false)}
        />
      )}
      <nav
        data-sidebar
        className={`fixed top-0 left-0 h-full bg-brand-dark text-gray-100 z-50 transition-all duration-300 
          ${isMobile ? "w-64" : sidebarWidth}
          ${isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"}
        `}
        style={{ boxShadow: "2px 0 8px rgba(0,0,0,0.3)" }}
      >
        <div className="h-full flex flex-col">
          <div
            className={`h-16 flex items-center border-b border-gray-600 ${
              isOpen ? "px-4" : "px-2"
            }`}
          >
            <Link
              to="/dashboard"
              onClick={handleChildClick}
              className="flex items-center gap-3 transition-all duration-200 hover:scale-105"
            >
              <div className="w-8 h-8 sidebar-active rounded-lg flex items-center justify-center transition-all duration-200 hover:shadow-lg">
                G
              </div>
              {(isOpen || isMobile) && (
                <span className="font-bold text-lg transition-colors duration-200">
                  GharPlans
                </span>
              )}
            </Link>
            {!isOpen && !isMobile && (
              <button
                data-sidebar-toggle
                onClick={() => onToggle(true)}
                className="ml-auto p-2 rounded-md transition-all duration-200 hover:bg-white/10 hover:scale-110 hover:shadow-md"
              >
                <MdMenuOpen size={20} />
              </button>
            )}
            {isMobile && isOpen && (
              <button
                data-sidebar-toggle
                onClick={() => onToggle(false)}
                className="ml-auto p-2 rounded-md transition-all duration-200 hover:bg-white/10 hover:scale-110 hover:shadow-md"
              >
                <MdMenuOpen size={20} />
              </button>
            )}
          </div>
          <ul className="flex-1 overflow-y-auto p-2 space-y-1">
            {allowedRoutes.map((item) => renderNavItem(item))}
          </ul>
        </div>
      </nav>
    </>
  );
}
